import React, { Component } from 'react';
import { connect } from 'react-redux';
import { setK3Scripts, getK3Script } from '../../actions/k3scriptAction';
import { getK3Docs } from '../../services/k3docService';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Badge,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { BsPencil, BsCheck2, BsX, BsTrash, BsCopy } from 'react-icons/bs';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/hljs';

// Register languages
SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('python', python);

class K3ScriptView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      script: props.selectedScript || { ...props.currentScript },
      preview: false,
      showDeleteConfirm: false,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      configFields: null,
      initialCustomOptions: {
        language: null,
        execution_target: null,
        script_type: null,
        status: null
      }
    };
  }

  componentDidMount() {
    // Fetch config fields using k3docService
    getK3Docs({ docMid: 'UI_CONFIG_FIELDS_K3SCRIPT' })
      .then(response => {
        const data = response.data.data.docs;
        console.log("UI_CONFIG_FIELDS_K3SCRIPT", data);
        if (data && data.length > 0 && data[0].content) {
          try {
            const configFields = JSON.parse(data[0].content);
            this.setState({ configFields }, this.syncCustomOptions);
          } catch (error) {
            console.error('Error parsing config fields:', error);
          }
        }
      })
      .catch(error => {
        console.error('Error fetching config fields:', error);
      });

    if (this.props.selectedScript) {
      this.setState({ script: this.props.selectedScript }, this.syncCustomOptions);
    } else {
      const { id } = this.props.match.params;
      if (id) {
        this.props.getK3Script(id);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.selectedScript && this.props.selectedScript !== prevProps.selectedScript) {
      this.setState({ script: this.props.selectedScript }, this.syncCustomOptions);
    } else if (this.props.currentScript && this.props.currentScript !== prevProps.currentScript) {
      this.setState({ script: { ...this.props.currentScript } }, this.syncCustomOptions);
    }
    // Nếu script hoặc configFields thay đổi, sync lại customOptions
    if (
      prevState.script !== this.state.script ||
      prevState.configFields !== this.state.configFields
    ) {
      this.syncCustomOptions();
    }
  }

  syncCustomOptions = () => {
    const { script, configFields } = this.state;
    if (!configFields || !script) return;
    const customOptions = { ...this.state.initialCustomOptions };
    [
      'language',
      'execution_target',
      'script_type',
      'status'
    ].forEach(field => {
      const value = script[field];
      const options = configFields[field]?.options || [];
      if (value && !options.some(opt => opt.value === value)) {
        customOptions[field] = value;
      } else {
        customOptions[field] = null;
      }
    });
    this.setState({ customOptions });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => {
      const newScript = {
        ...prevState.script,
        [name]: value
      };
      // Nếu user chọn giá trị khác, giữ customOptions cho đến khi lưu/hủy
      return { script: newScript };
    });
  };

  startEdit = () => {
    const { script, configFields } = this.state;
    const initialCustomOptions = {};
    ['language', 'execution_target', 'script_type', 'status'].forEach(field => {
      const value = script[field];
      const options = configFields && configFields[field]?.options ? configFields[field].options : [];
      if (value && !options.some(opt => opt.value === value)) {
        initialCustomOptions[field] = value;
      } else {
        initialCustomOptions[field] = null;
      }
    });
    this.setState({ isEditing: true, initialCustomOptions });
  };

  cancelEdit = () => {
    this.setState({ isEditing: false, initialCustomOptions: {
      language: null,
      execution_target: null,
      script_type: null,
      status: null
    }});
  };

  handleSubmit = async (e, isNewVersion = false) => {
    e.preventDefault();
    try {
      const { _id, createdAt, updatedAt, ...scriptData } = this.state.script;
      const updatedScript = {
        ...scriptData,
        _id: isNewVersion ? undefined : _id,
        tags: Array.isArray(scriptData.tags) ? scriptData.tags : scriptData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        metadata: typeof scriptData.metadata === 'string' ? JSON.parse(scriptData.metadata) : scriptData.metadata
      };

      // Handle version_note logic
      if (isNewVersion && updatedScript.friendly_version === scriptData.friendly_version) {
        // If saving as new version with same friendly_version, set both to empty
        updatedScript.friendly_version = '';
        updatedScript.version_note = '';
      } else if (!updatedScript.friendly_version) {
        // If friendly_version is empty, set version_note to empty string
        updatedScript.version_note = '';
      }

      await this.props.setK3Scripts([{ _id, ...updatedScript }], { generate_version: isNewVersion });
      this.setState({ isEditing: false, initialCustomOptions: {
        language: null,
        execution_target: null,
        script_type: null,
        status: null
      }});
    } catch (error) {
      console.error('Error updating script:', error);
    }
  };

  handleDelete = async () => {
    try {
      const { _id } = this.state.script;
      await this.props.setK3Scripts([{ _id, is_deleted: true }]);
      this.setState({ showDeleteConfirm: false });
      if (this.props.onHide) {
        this.props.onHide();
      } else {
        this.props.history.push('/k3scripts');
      }
    } catch (error) {
      console.error('Error deleting script:', error);
    }
  };

  handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    this.setState({
      showToast: true,
      toastMessage: `${fieldName} copied to clipboard!`,
      toastType: 'success'
    });
    setTimeout(() => {
      this.setState({ showToast: false });
    }, 3000);
  };

  renderContent() {
    const { script, isEditing, configFields, initialCustomOptions } = this.state;

    if (!configFields) {
      return null; // Or loading spinner
    }

    // Helper to merge value into options if missing
    const mergeOptions = (options, customValue) => {
      let merged = options;
      if (customValue && !options.some(opt => opt.value === customValue)) {
        merged = [{ value: customValue, label: customValue }, ...options];
      }
      return merged;
    };

    return (
      <>
        <Row className="mb-4">
          <Col className="d-flex justify-content-between align-items-center">
            <h1>
              {isEditing ? (
                <Form.Control
                  type="text"
                  name="title"
                  value={script.title}
                  onChange={this.handleChange}
                  className="h1"
                />
              ) : (
                script.title
              )}
            </h1>
            {!this.props.selectedScript && (
              <div>
                <Button
                  variant={isEditing ? "success" : "outline-primary"}
                  size="sm"
                  onClick={isEditing ? this.handleSubmit : this.startEdit}
                >
                  {isEditing ? <><BsCheck2 className="me-1" /> Save</> : <><BsPencil className="me-1" /> Edit</>}
                </Button>
                {isEditing && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={this.cancelEdit}
                    className="ms-2"
                  >
                    <BsX className="me-1" /> Cancel
                  </Button>
                )}
              </div>
            )}
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Script MID</Form.Label>
              <div style={{ position: 'relative' }}>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="script_mid"
                    value={script.script_mid}
                    onChange={this.handleChange}
                    style={{ paddingRight: '40px' }}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    value={script.script_mid}
                    readOnly
                    style={{ paddingRight: '40px' }}
                  />
                )}
                <Button 
                  variant="outline-secondary" 
                  onClick={() => this.handleCopy(script.script_mid, 'Script MID')}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0.25rem 0.5rem',
                    border: 'none',
                    background: 'transparent'
                  }}
                >
                  <BsCopy />
                </Button>
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>ID</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  value={script._id}
                  readOnly
                  style={{ paddingRight: '40px' }}
                />
                <Button 
                  variant="outline-secondary" 
                  onClick={() => this.handleCopy(script._id, 'ID')}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0.25rem 0.5rem',
                    border: 'none',
                    background: 'transparent'
                  }}
                >
                  <BsCopy />
                </Button>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4">
          <Form.Label>Description</Form.Label>
          <div style={{ position: 'relative' }}>
            {isEditing ? (
              <Form.Control
                as="textarea"
                name="description"
                value={script.description}
                onChange={this.handleChange}
                rows={3}
                style={{ paddingRight: '40px' }}
              />
            ) : (
              <Form.Control
                as="textarea"
                value={script.description}
                readOnly
                rows={3}
                style={{ paddingRight: '40px' }}
              />
            )}
            <Button 
              variant="outline-secondary" 
              onClick={() => this.handleCopy(script.description, 'Description')}
              style={{
                position: 'absolute',
                right: '5px',
                top: '5px',
                padding: '0.25rem 0.5rem',
                border: 'none',
                background: 'transparent'
              }}
            >
              <BsCopy />
            </Button>
          </div>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Script Code</Form.Label>
          <div style={{ position: 'relative' }}>
            {isEditing ? (
              <Form.Control
                as="textarea"
                name="script_code"
                value={script.script_code}
                onChange={this.handleChange}
                rows={15}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  backgroundColor: '#1e1e1e',
                  color: '#ffffff',
                  border: '1px solid #3c3c3c',
                  paddingRight: '40px'
                }}
              />
            ) : (
              <div style={{ 
                maxHeight: '500px', 
                overflowY: 'auto',
                backgroundColor: '#1e1e1e',
                borderRadius: '0.25rem',
                border: '1px solid #3c3c3c',
                position: 'relative'
              }}>
                <SyntaxHighlighter 
                  language={script.language || 'python'} 
                  style={tomorrow}
                  customStyle={{ 
                    margin: 0,
                    padding: '1rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff'
                  }}
                  showLineNumbers
                  lineNumberStyle={{ color: '#858585' }}
                >
                  {script.script_code || ''}
                </SyntaxHighlighter>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => this.handleCopy(script.script_code, 'Script Code')}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '5px',
                    padding: '0.25rem 0.5rem',
                    border: 'none',
                    background: 'transparent',
                    color: '#ffffff'
                  }}
                >
                  <BsCopy />
                </Button>
              </div>
            )}
          </div>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Reason</Form.Label>
          <div style={{ position: 'relative' }}>
            {isEditing ? (
              <Form.Control
                as="textarea"
                name="reason"
                value={script.reason}
                onChange={this.handleChange}
                rows={3}
                style={{ paddingRight: '40px' }}
              />
            ) : (
              <Form.Control
                as="textarea"
                value={script.reason}
                readOnly
                rows={3}
                style={{ paddingRight: '40px' }}
              />
            )}
            <Button 
              variant="outline-secondary" 
              onClick={() => this.handleCopy(script.reason, 'Reason')}
              style={{
                position: 'absolute',
                right: '5px',
                top: '5px',
                padding: '0.25rem 0.5rem',
                border: 'none',
                background: 'transparent'
              }}
            >
              <BsCopy />
            </Button>
          </div>
        </Form.Group>

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Created At</Form.Label>
              <Form.Control
                type="text"
                value={new Date(script.createdAt).toLocaleString()}
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Updated At</Form.Label>
              <Form.Control
                type="text"
                value={new Date(script.updatedAt).toLocaleString()}
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Version</Form.Label>
              <Form.Control
                type="text"
                value={script.version}
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Friendly Version</Form.Label>
              {isEditing ? (
                <Form.Control
                  type="text"
                  name="friendly_version"
                  value={script.friendly_version}
                  onChange={this.handleChange}
                />
              ) : (
                <Form.Control
                  type="text"
                  value={script.friendly_version}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
        </Row>

        {script.friendly_version && (
          <Row className="mb-4">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Version Note</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="version_note"
                    value={script.version_note}
                    onChange={this.handleChange}
                    rows={3}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    value={script.version_note}
                    readOnly
                    rows={3}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{configFields.language.label}</Form.Label>
              {isEditing ? (
                <Form.Select
                  name="language"
                  value={script.language}
                  onChange={this.handleChange}
                >
                  <option value="">Select Language</option>
                  {mergeOptions(configFields.language.options, initialCustomOptions.language).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  value={script.language}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{configFields.execution_target.label}</Form.Label>
              {isEditing ? (
                <Form.Select
                  name="execution_target"
                  value={script.execution_target}
                  onChange={this.handleChange}
                >
                  <option value="">Select Target</option>
                  {mergeOptions(configFields.execution_target.options, initialCustomOptions.execution_target).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  value={script.execution_target}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{configFields.script_type.label}</Form.Label>
              {isEditing ? (
                <Form.Select
                  name="script_type"
                  value={script.script_type}
                  onChange={this.handleChange}
                >
                  <option value="">Select Type</option>
                  {mergeOptions(configFields.script_type.options, initialCustomOptions.script_type).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  value={script.script_type}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{configFields.status.label}</Form.Label>
              {isEditing ? (
                <Form.Select
                  name="status"
                  value={script.status}
                  onChange={this.handleChange}
                >
                  {mergeOptions(configFields.status.options, initialCustomOptions.status).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  value={script.status}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Is Template</Form.Label>
              {isEditing ? (
                <Form.Check
                  type="switch"
                  name="is_template"
                  checked={script.is_template || false}
                  onChange={(e) => this.handleChange({ target: { name: 'is_template', value: e.target.checked } })}
                  label={script.is_template ? "This is a template script" : "This is not a template script"}
                />
              ) : (
                <Form.Control
                  type="text"
                  value={script.is_template ? "Yes" : "No"}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>From Template</Form.Label>
              {isEditing ? (
                <Form.Select
                  name="from_template_mid"
                  value={script.from_template_mid || ''}
                  onChange={this.handleChange}
                >
                  <option value="">Select Template</option>
                  {this.props.scripts
                    .filter(s => s.is_template)
                    .map(s => (
                      <option key={s.script_mid} value={s.script_mid}>
                        {s.script_mid} - {s.title}
                      </option>
                    ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  value={script.from_template_mid ? 
                    `${script.from_template_mid} - ${this.props.scripts.find(s => s.script_mid === script.from_template_mid)?.title || ''}` 
                    : 'None'}
                  readOnly
                />
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4">
          <Form.Label>Tags</Form.Label>
          {isEditing ? (
            <Form.Control
              type="text"
              name="tags"
              value={Array.isArray(script.tags) ? script.tags.join(', ') : script.tags}
              onChange={this.handleChange}
              placeholder="tag1, tag2, tag3"
            />
          ) : (
            <div>
              {Array.isArray(script.tags) ? script.tags.map(tag => (
                <Badge key={tag} bg="secondary" className="me-1">
                  {tag}
                </Badge>
              )) : script.tags}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Metadata</Form.Label>
          <div style={{ position: 'relative' }}>
            {isEditing ? (
              <Form.Control
                as="textarea"
                name="metadata"
                value={typeof script.metadata === 'object' ? JSON.stringify(script.metadata, null, 2) : script.metadata}
                onChange={this.handleChange}
                rows={5}
                style={{ paddingRight: '40px' }}
              />
            ) : (
              <Form.Control
                as="textarea"
                value={typeof script.metadata === 'object' ? JSON.stringify(script.metadata, null, 2) : script.metadata}
                readOnly
                rows={5}
                style={{ paddingRight: '40px' }}
              />
            )}
            <Button 
              variant="outline-secondary" 
              onClick={() => this.handleCopy(typeof script.metadata === 'object' ? JSON.stringify(script.metadata, null, 2) : script.metadata, 'Metadata')}
              style={{
                position: 'absolute',
                right: '5px',
                top: '5px',
                padding: '0.25rem 0.5rem',
                border: 'none',
                background: 'transparent'
              }}
            >
              <BsCopy />
            </Button>
          </div>
        </Form.Group>
      </>
    );
  }

  render() {
    const { selectedScript, show, onHide } = this.props;
    const { showDeleteConfirm, isEditing, showToast, toastMessage, toastType } = this.state;

    if (selectedScript) {
      return (
        <>
          <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton className="pl-5 pr-5">
              <div className="d-flex justify-content-between align-items-center w-100">
                <Modal.Title>Script Details</Modal.Title>
                <div>
                  {isEditing ? (
                    <>
                      <Button 
                        variant="success"
                        className="me-2"
                        onClick={(e) => this.handleSubmit(e, true)}
                      >
                        <BsCopy className="me-1" /> Save as New Version
                      </Button>
                      <Button 
                        variant="primary"
                        onClick={(e) => this.handleSubmit(e, false)}
                      >
                        <BsCheck2 className="me-1" /> Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={this.startEdit}
                    >
                      <BsPencil className="me-1" /> Edit
                    </Button>
                  )}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="pl-5 pr-5">
              {this.renderContent()}
            </Modal.Body>
            <Modal.Footer className="p-5 pt-0">
              <Button variant="danger" onClick={() => this.setState({ showDeleteConfirm: true })}>
                <BsTrash className="me-1" /> Delete
              </Button>
              <Button variant="secondary" onClick={onHide}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showDeleteConfirm} onHide={() => this.setState({ showDeleteConfirm: false })}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete this script? This action cannot be undone.
            </Modal.Body>
            <Modal.Footer className="pl-5 pr-5">
              <Button variant="secondary" onClick={() => this.setState({ showDeleteConfirm: false })}>
                Cancel
              </Button>
              <Button variant="danger" onClick={this.handleDelete}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>

          <ToastContainer position="top-end" className="p-3">
            <Toast 
              show={showToast} 
              onClose={() => this.setState({ showToast: false })}
              bg={toastType}
              delay={3000}
              autohide
            >
              <Toast.Header closeButton={false}>
                <strong className="me-auto">Success</strong>
              </Toast.Header>
              <Toast.Body className="text-white">
                {toastMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </>
      );
    }

    return (
      <div className="container-fluid p-4">
        {this.renderContent()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  currentScript: state.k3scriptReducer.currentScript,
  scripts: state.k3scriptReducer.scripts
});

export default connect(mapStateToProps, { setK3Scripts, getK3Script })(K3ScriptView); 